import { useEffect, useMemo, useState, type PropsWithChildren } from "react";

import clsx from "clsx";
import { orderBy } from "lodash";
import { useEffectOnce } from "usehooks-ts";

import { LoadingScreen, LoadingSlide } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { AnnotationsFlyout } from "@features/custom-annotations";
import {
  type ClientSetting,
  type ModelMetadata,
} from "@features/mmm/components/explore-budgets/client-setting/types";
import { useIsAutoblessedModelsEnabled } from "@features/mmm/components/media-plan/hooks/use-is-autoblessed-models-enabled";
import { useMediaPlanData } from "@features/mmm/components/media-plan/hooks/use-media-plan";
import { MinimizedTopBar, TopBar } from "@features/mmm/components/top-bars";
import {
  ActiveTab,
  ActiveTabOrdering,
  PrettyActiveTab,
  useMMMActiveTab,
} from "@features/mmm/hooks/mmm-url-state";
import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { useLoadClientSettings } from "@features/mmm/hooks/use-load-client-settings";
import { useMmmAnnotations } from "@features/mmm/hooks/use-mmm-annotations";
import { useMMMNotifications } from "@features/mmm/hooks/use-mmm-notifications";
import { FETCH_MODEL_INSIGHTS_MODEL_METADATA } from "@gql/mmm-model-insights";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import {
  useFeatureFlags,
  useNorthbeamLazyQuery,
  useNorthbeamQuery,
} from "@hooks/http";
import { useIntersectionObserver } from "@hooks/use-intersection-observer";
import { usePrevious } from "@hooks/use-previous";
import { prettyLogEvent } from "@internal/analytics";
import {
  MMMState,
  type FetchModelInsightsModelMetadata,
  type FetchModelInsightsModelMetadataVariables,
  type FetchUniversalMMMData,
} from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import { MMMClientState, type MMMClientCycle } from "@north-beam/nb-common";

import { BudgetPacingNotificationPanel } from "../../features/mmm/components/budget-pacing/notification-panel";

import { MMM_GLOBAL_DATA_QUERY } from "./explore-budgets-v2/explore-budgets-queries";
import { ExploreBudgetsTab } from "./explore-budgets-v2/explore-budgets-tab";
import { useAlerts } from "./hooks/use-alerts";
import { useAvailableBudgetMixOptions } from "./hooks/use-available-budget-mix-options";
import { useAvailableDataColumns } from "./hooks/use-available-data-columns";
import { useAvailableKpis } from "./hooks/use-available-kpis";
import { useClientActiveSettings } from "./hooks/use-client-active-settings";
import { useClientCycles } from "./hooks/use-client-cycles";
import { useClientSettings } from "./hooks/use-client-settings";
import { useLoadBudgetMixes } from "./hooks/use-load-budget-mixes";
import { useMMMClient } from "./hooks/use-mmm-client";
import { useModelMetadata } from "./hooks/use-model-metadata";
import { useSelectedCycle } from "./hooks/use-selected-cycle";
import { PendingMediaPlan } from "./info-pages/pending-media-plan";
import { PopulatingData } from "./info-pages/populating-data";
import { LandingPage } from "./landing-page";
import { LearnAboutMMM } from "./learn-about-mmm";
import { MediaPlanTab } from "./media-plan/media-plan-tab";
import { ModelInsightsTab } from "./model-insights/model-insights-tab";
import { PerformanceTrackingTab } from "./performance-tracking/performance-tracking-tab";
import { SummaryTab } from "./summary/summary-tab";
import {
  isModelInsightsTabEnabled,
  isPerformanceTrackingTabEnabled,
  isSummaryEnabled,
  isValidActiveTab,
} from "./tab-states";

export const MMMPage = () => {
  const { selectedCycle, setSelectedCycle } = useSelectedCycle();
  const [, setMMMClient] = useMMMClient();
  const [, setAvailableBudgetMixOptions] = useAvailableBudgetMixOptions();
  const [, setClientCycles] = useClientCycles();
  const [, setModelMetadata] = useModelMetadata();
  const [, setActiveClientSettings] = useClientActiveSettings();
  const [, setAlerts] = useAlerts();
  const { clientSettings: cycleClientSettings } = useClientSettings();
  const { adoptedBudgetMix } = useAdoptedBudgetMix();
  const { user } = useUser();

  const { hasFeatureFlag } = useFeatureFlags();
  const hasAdoptedBudgetMix = !!adoptedBudgetMix;
  const areAutoblessedModelsEnabled = useIsAutoblessedModelsEnabled();

  useLoadBudgetMixes();

  const { loading: clientSettingsLoading } =
    useLoadClientSettings(selectedCycle);

  const { activeTab, setActiveTab } = useMMMActiveTab();
  const getActiveTabIndex = (tab: ActiveTab | null) => {
    return tab ? ActiveTabOrdering.indexOf(tab) : 0;
  };

  const [couldNotFindSelectedCycle, setCouldNotFindSelectedCycle] =
    useState(false);

  const [topLevelFiltersContainerElRef, isMinimizedTopBarVisible] =
    useIntersectionObserver();

  const [notifications] = useMMMNotifications();
  const modelInsightsEnabled = isModelInsightsTabEnabled(
    selectedCycle,
    cycleClientSettings,
  );
  const performanceTrackingEnabled = isPerformanceTrackingTabEnabled(
    selectedCycle,
    hasAdoptedBudgetMix,
  );
  const summaryEnabled =
    isSummaryEnabled(selectedCycle, user.timezone) && adoptedBudgetMix !== null;
  const showPopulatingData = [
    MMMClientState.POPULATING_DATA,
    MMMClientState.REVIEWING_MODEL,
  ].includes(selectedCycle.state);

  const { isLoadingKpis, loadKpis } = useAvailableKpis();
  const { isLoadingDataColumns, loadDataColumns } = useAvailableDataColumns();

  const {
    loadData: loadModelMetadata,
    data: { loading: modelMetadataLoading },
  } = useNorthbeamLazyQuery<
    FetchModelInsightsModelMetadata,
    FetchModelInsightsModelMetadataVariables
  >(FETCH_MODEL_INSIGHTS_MODEL_METADATA, {
    onCompleted(data) {
      const metadata = data.me.mmm.modelInsightsModelMetadata;
      setModelMetadata({
        ...metadata,
        kpi_formulas: orderBy(metadata.kpi_formulas, "friendly_name"),
      } as unknown as ModelMetadata);
    },
  });

  const { data, loading } = useNorthbeamQuery<FetchUniversalMMMData>(
    MMM_GLOBAL_DATA_QUERY,
    {
      onCompleted(data) {
        setMMMClient(data.me.mmm.client);
        setAvailableBudgetMixOptions(data.me.mmm.availableBudgetMixTypes);
        setAlerts(data.me.mmm.alerts);
        const { cycles } = data.me.mmm;
        const foundCycleFromUrl = cycles.find(
          ({ id }) => id === selectedCycle.id,
        );
        if (
          selectedCycle.id !== "-1" &&
          foundCycleFromUrl &&
          selectedCycle.state === MMMClientState.NOT_SET
        ) {
          // initial load and the selected cycle id is in the URL (there will be a
          // brief time where all the cycles have not loaded and we add placeholder
          // initial values until the network request is complete)
          setCouldNotFindSelectedCycle(false);
          setSelectedCycle(foundCycleFromUrl as unknown as MMMClientCycle);
        } else if (!foundCycleFromUrl) {
          // current cycle is not defined by the URL state and needs to be
          // set to a default
          const currentCycle = cycles.find((cycle) => cycle.is_current);
          if (currentCycle) {
            setCouldNotFindSelectedCycle(false);
            setSelectedCycle(currentCycle as unknown as MMMClientCycle);
          } else if (cycles[0]) {
            // Get latest
            setCouldNotFindSelectedCycle(false);
            setSelectedCycle(cycles[0] as unknown as MMMClientCycle);
          } else {
            setCouldNotFindSelectedCycle(true);
          }
        }

        // TODO: fix typing
        setClientCycles(cycles as unknown as MMMClientCycle[]);
        setActiveClientSettings([
          ...data.me.mmm.activeClientSettings,
        ] as unknown as ClientSetting[]);
      },
    },
  );

  useEffect(() => {
    const cycles = data?.me.mmm.cycles ?? [];
    const selectedCycleNotSet = !selectedCycle || selectedCycle.id === "-1";

    if (couldNotFindSelectedCycle && selectedCycleNotSet && cycles.length) {
      setSelectedCycle(cycles[0] as unknown as MMMClientCycle);
    }
  }, [
    data?.me.mmm.cycles,
    couldNotFindSelectedCycle,
    selectedCycle,
    setSelectedCycle,
  ]);

  const enableMmmMediaPlanTab =
    hasFeatureFlag("enableMmmMediaPlanTab") && !loading;

  const prevSelectedClientCycle = usePrevious(selectedCycle);

  useEffect(() => {
    if (
      selectedCycle?.id !== "-1" &&
      selectedCycle?.id !== prevSelectedClientCycle?.id
    ) {
      const performanceTabEnabled = isPerformanceTrackingTabEnabled(
        selectedCycle,
        hasAdoptedBudgetMix,
      );
      const summaryTabEnabled = isSummaryEnabled(selectedCycle, user.timezone);

      if (
        !activeTab &&
        selectedCycle.state === MMMClientState.TRACKING_MIX &&
        performanceTabEnabled
      ) {
        setActiveTab(ActiveTab.PERFORMANCE_TRACKING);
      } else if (
        !activeTab &&
        selectedCycle.state === MMMClientState.REVIEWING_PERIOD &&
        summaryTabEnabled
      ) {
        setActiveTab(ActiveTab.SUMMARY);
      } else if (!activeTab) {
        setActiveTab(ActiveTab.EXPLORE_MIXES);
      } else {
        if (
          data?.me &&
          !isValidActiveTab(
            activeTab,
            selectedCycle,
            data.me.mmm.activeClientSettings as unknown as ClientSetting[],
            user.isAdmin,
            hasAdoptedBudgetMix,
            user.timezone,
          )
        ) {
          setActiveTab(ActiveTab.EXPLORE_MIXES);
        }
      }

      loadKpis({ variables: { id: selectedCycle.id } });
      loadDataColumns({ variables: { cycleId: selectedCycle.id } });
    }
  }, [
    loadKpis,
    prevSelectedClientCycle,
    selectedCycle,
    modelInsightsEnabled,
    loadModelMetadata,
    user.isAdmin,
    user.timezone,
    activeTab,
    data?.me,
    setActiveTab,
    loadDataColumns,
    hasAdoptedBudgetMix,
  ]);

  useEffect(() => {
    if (selectedCycle?.id !== "-1" && modelInsightsEnabled) {
      loadModelMetadata({ variables: { cycleId: selectedCycle.id } });
    }
  }, [loadModelMetadata, modelInsightsEnabled, selectedCycle.id]);

  useEffectOnce(() => {
    prettyLogEvent.visited({ page: Pages.MMM, user });
  });

  const {
    annotations,
    fetchAnnotations,
    isAnnotationsLoading,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
  } = useMmmAnnotations();

  const { mediaPlanList } = useMediaPlanData();
  // A media plan is being blessed has a media plan draft and is in the blessing model state
  const isPendingMediaPlan = useMemo(() => {
    if (areAutoblessedModelsEnabled) {
      return false;
    }
    const selectedMediaPlan = mediaPlanList.find(
      (mediaPlan) => mediaPlan.id === selectedCycle.id,
    );

    return (
      selectedMediaPlan?.media_plan_draft_id !== null &&
      selectedMediaPlan?.state === MMMState.blessing_model
    );
  }, [mediaPlanList, selectedCycle, areAutoblessedModelsEnabled]);

  if (
    loading ||
    clientSettingsLoading ||
    isLoadingKpis ||
    isLoadingDataColumns ||
    modelMetadataLoading
  ) {
    return <LoadingScreen />;
  }

  if (selectedCycle.breakdowns.length === 0) {
    return <LandingPage />;
  } else if (data?.me.mmm.cycles.length === 0) {
    return <LearnAboutMMM />;
  } else if (!selectedCycle) {
    return <LoadingScreen />;
  }

  return (
    <>
      <AnnotationsFlyout
        annotations={annotations}
        fetchAnnotations={fetchAnnotations}
        isAnnotationsLoading={isAnnotationsLoading}
        createAnnotation={createAnnotation}
        updateAnnotation={updateAnnotation}
        deleteAnnotation={deleteAnnotation}
        isMmm
      />
      <MinimizedTopBar isVisible={isMinimizedTopBarVisible} />
      <div ref={topLevelFiltersContainerElRef}>
        <TopBar />
      </div>
      <div className={clsx("m-5", { "justify-center": !notifications.length })}>
        {!selectedCycle || clientSettingsLoading ? (
          <LoadingScreen />
        ) : (
          <>
            <BudgetPacingNotificationPanel />
            {activeTab === ActiveTab.MEDIA_PLAN && enableMmmMediaPlanTab ? (
              <MediaPlanTab
                goBack={() => setActiveTab(ActiveTab.EXPLORE_MIXES)}
              />
            ) : isPendingMediaPlan ? (
              <PendingMediaPlan />
            ) : showPopulatingData ? (
              <PopulatingData />
            ) : (
              <TabGroup selectedIndex={getActiveTabIndex(activeTab)}>
                <TabList className="flex flex-row gap-1 relative">
                  {/* Did some fun stuff to get the top border line */}
                  <div
                    className={clsx(
                      "absolute -bottom-2 w-full",
                      "border-t h-[9px] border-v2-secondary z-[1]",
                    )}
                  />
                  <MMMTab onClick={() => setActiveTab(ActiveTab.EXPLORE_MIXES)}>
                    {PrettyActiveTab[ActiveTab.EXPLORE_MIXES]}
                  </MMMTab>
                  <MMMTab
                    onClick={() => setActiveTab(ActiveTab.MODEL_INSIGHTS)}
                    isEnabled={modelInsightsEnabled}
                    disabledText="The insights tab is not available until a setting is created"
                  >
                    {PrettyActiveTab[ActiveTab.MODEL_INSIGHTS]}
                  </MMMTab>
                  <MMMTab
                    onClick={() => setActiveTab(ActiveTab.PERFORMANCE_TRACKING)}
                    isEnabled={performanceTrackingEnabled}
                    disabledText="Adopt a budget to view this tab"
                  >
                    {PrettyActiveTab[ActiveTab.PERFORMANCE_TRACKING]}
                  </MMMTab>
                  <MMMTab
                    isEnabled={summaryEnabled}
                    onClick={() => setActiveTab(ActiveTab.SUMMARY)}
                    disabledText="The Recap Performance tab is not available until the period has ended"
                  >
                    {PrettyActiveTab[ActiveTab.SUMMARY]}
                  </MMMTab>
                </TabList>
                <TabPanels
                  className={clsx(
                    "flex flex-col border border-t-0 border-v2-secondary rounded-md rounded-tl-none rounded-tr-none bg-v2-primary text-v2-primary",
                  )}
                >
                  <MMMPanel className="m-4">
                    {!selectedCycle || selectedCycle.date_range.id === -1 ? (
                      <MMMPanelLoading />
                    ) : (
                      <ExploreBudgetsTab />
                    )}
                  </MMMPanel>
                  <MMMPanel className="m-4">
                    {modelInsightsEnabled ? (
                      <ModelInsightsTab />
                    ) : (
                      <MMMPanelLoading />
                    )}
                  </MMMPanel>
                  <MMMPanel>
                    {performanceTrackingEnabled ? (
                      <PerformanceTrackingTab />
                    ) : (
                      <MMMPanelLoading />
                    )}
                  </MMMPanel>
                  <MMMPanel className="m-4">
                    {summaryEnabled ? <SummaryTab /> : <MMMPanelLoading />}
                  </MMMPanel>
                </TabPanels>
              </TabGroup>
            )}
          </>
        )}
      </div>
    </>
  );
};

function MMMPanel({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <TabPanel className={clsx("flex-1 flex flex-col", className)}>
      {children}
    </TabPanel>
  );
}

function MMMPanelLoading() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center">
      <LoadingSlide />
    </div>
  );
}

function MMMTab({
  onClick,
  className,
  isEnabled = true,
  children,
  disabledText,
}: PropsWithChildren<{
  onClick: () => void;
  className?: string;
  isEnabled?: boolean;
  disabledText?: string;
}>) {
  const isDisabled = !isEnabled;
  return (
    <Tab
      onClick={() => !isDisabled && onClick()}
      className={({ selected }) =>
        clsx(
          "w-60 h-10 flex items-center z-2 text-sm tracking-wide",
          "border px-2.5 rounded-t-lg",
          {
            "border-v2-secondary bg-v2-action-tertiary text-v2-primary":
              !selected,
            "cursor-pointer hover:border-t-primary hover:border-x-v2-action hover:bg-v2-highlight-information":
              !selected && isEnabled,
            "bg-v2-primary border-t-v2-secondary border-x-v2-secondary border-b-v2-inverse text-v2-primary":
              selected,
            "text-v2-action font-semibold": selected && isEnabled,
            "cursor-not-allowed text-v2-secondary": isDisabled,
          },
          className,
        )
      }
    >
      {isDisabled ? (
        <GenericTooltip destroyTooltipOnHide content={disabledText}>
          {children}
        </GenericTooltip>
      ) : (
        children
      )}
    </Tab>
  );
}
